import type { ComponentType } from 'react';
import {
  Award,
  BadgeCheck,
  BarChart3,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Car,
  ClipboardList,
  CreditCard,
  Home,
  LayoutGrid,
  Link as LinkIcon,
  MapPin,
  Megaphone,
  Shield,
  ShoppingBag,
  Sparkles,
  Star,
  Store,
  Tag,
  TrendingUp,
  User,
  Wrench,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PERMISSIONS } from '@/client/config/permissions';
import type {
  AdminCatalogApp,
  AppCatalogStatus,
  AppsCatalogMeta,
  PublicCatalogApp,
} from '@/client/types/apps-catalog';

/** Permission codes seeded by ff-api business migration 0428. */
export const APPS_CATALOG_PERMS = {
  read: PERMISSIONS.apps.catalogRead,
  write: PERMISSIONS.apps.catalogWrite,
  publish: PERMISSIONS.apps.catalogPublish,
} as const;

/** Surface the API's publish `warnings` (unrouted key) as toasts that linger. */
export const showCatalogWarnings = (warnings: string[] | undefined) => {
  (warnings ?? []).forEach((warning) =>
    toast.warning('Published without an ff-ui route', { description: warning, duration: 12_000 }),
  );
};

export const STATUS_LABELS: Record<AppCatalogStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  coming_soon: 'Coming soon',
  unpublished: 'Unpublished',
};

const STATUS_CLASSES: Record<AppCatalogStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-800',
  coming_soon: 'bg-amber-100 text-amber-800',
  unpublished: 'bg-red-50 text-red-700',
};

export const statusLabel = (status: string) =>
  STATUS_LABELS[status as AppCatalogStatus] ?? status;

export const StatusPill = ({ status, className }: { status: string; className?: string }) => (
  <span
    className={cn(
      'inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
      STATUS_CLASSES[status as AppCatalogStatus] ?? 'bg-gray-100 text-gray-700',
      className,
    )}
  >
    {statusLabel(status)}
  </span>
);

export const SITE_LABELS: Record<string, string> = {
  marketplace: 'Marketplace',
  homes: 'Homes',
};

export const siteLabel = (site: string) => SITE_LABELS[site] ?? site;

/** Whether ff-ui can open this key today. Unknown (custom) keys cannot. */
export const isRoutedKey = (meta: AppsCatalogMeta | undefined, appKey: string) =>
  !!meta?.known_app_keys.some((k) => k.app_key === appKey && k.routed);

/** Mirrors the API's publish warning so the dialog can warn before the call. */
export const unroutedPublishWarning = (appKey: string) =>
  `ff-ui has no route for ${appKey}. The store will list it, but it cannot be opened until ` +
  'ff-ui ships one; Coming soon is the safer status until then.';

// Lucide names from the API's icon allowlist (config/apps_catalog.py). An
// unknown name falls back to the grid icon, as ff-ui does.
const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  award: Award,
  'badge-check': BadgeCheck,
  'bar-chart-3': BarChart3,
  briefcase: Briefcase,
  building: Building,
  'building-2': Building2,
  calendar: Calendar,
  car: Car,
  'clipboard-list': ClipboardList,
  'credit-card': CreditCard,
  home: Home,
  'layout-grid': LayoutGrid,
  link: LinkIcon,
  'map-pin': MapPin,
  megaphone: Megaphone,
  shield: Shield,
  'shopping-bag': ShoppingBag,
  sparkles: Sparkles,
  star: Star,
  store: Store,
  tag: Tag,
  'trending-up': TrendingUp,
  user: User,
  wrench: Wrench,
  zap: Zap,
};

export const CatalogIcon = ({ name, className }: { name: string; className?: string }) => {
  const Icon = ICONS[name] ?? LayoutGrid;
  return <Icon className={className} />;
};

type TileApp = Pick<
  PublicCatalogApp,
  'name' | 'description' | 'icon' | 'icon_url' | 'marketing_label' | 'status' | 'price_label'
> &
  Partial<Pick<PublicCatalogApp, 'show_in_grid' | 'show_in_store'>>;

/** An app tile, roughly as ff-ui's header grid draws it. */
export const CatalogTile = ({ app, showFlags = false }: { app: TileApp; showFlags?: boolean }) => {
  const hiddenFromPublic = app.status === 'draft' || app.status === 'unpublished';
  return (
    <div
      className={cn(
        'flex h-full gap-3 rounded-lg border bg-white p-3 transition-colors hover:border-blue-300',
        hiddenFromPublic && 'border-dashed bg-gray-50',
      )}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
        {app.icon_url ? (
          <img src={app.icon_url} alt="" className="h-6 w-6 object-contain" />
        ) : (
          <CatalogIcon name={app.icon} className="h-5 w-5" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-gray-900">{app.name || 'Untitled'}</span>
          {app.marketing_label ? (
            <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              {app.marketing_label}
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{app.description || '—'}</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {app.status !== 'published' ? <StatusPill status={app.status} className="text-[10px]" /> : null}
          {app.price_label ? (
            <span className="text-[10px] font-medium text-gray-600">{app.price_label}</span>
          ) : null}
          {showFlags && app.show_in_grid === false ? (
            <span className="text-[10px] text-gray-400">not in grid</span>
          ) : null}
          {showFlags && app.show_in_store === false ? (
            <span className="text-[10px] text-gray-400">not in store</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** The catalog stores naive UTC; treat a value without an offset as UTC. */
export const parseCatalogDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasOffset ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatCatalogDate = (value: string | null | undefined) => {
  const date = parseCatalogDate(value);
  return date
    ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
};

/** Why a stored marketing label is or isn't showing right now. */
export const labelWindowState = (
  app: Pick<
    AdminCatalogApp,
    'marketing_label' | 'marketing_label_live' | 'marketing_label_starts_at' | 'marketing_label_ends_at'
  >,
): 'none' | 'live' | 'scheduled' | 'ended' => {
  if (!app.marketing_label) return 'none';
  if (app.marketing_label_live) return 'live';
  const start = parseCatalogDate(app.marketing_label_starts_at);
  if (start && start.getTime() > Date.now()) return 'scheduled';
  return 'ended';
};
